import {
  LayoutGrid, 
  BrainCircuit, 
  AudioLines, 
  Bot, 
  Blocks, 
  Globe,
  Fingerprint,
  ShieldCheck,
  FileCheck2,
  MicVocal,
  MessageSquareDiff,
  Wrench,
  History,
  Activity,
  Wallet,
  Crown,
  LifeBuoy,
  Info,
  Send,
  ChevronUp,
  ChevronDown,
  // Added for existing references
  LayoutDashboard,
  FlaskConical,
  Database,
  HelpCircle,
  Mail,
  Home,
  Sparkles
} from 'lucide-react';

import { APP_PREFIX_PATH } from 'configs/AppConfig'
import { getUserRole } from 'utils/getUserRole';
import { SEC_AUTH_M_TK } from 'constants/AuthConstant';
import {
   SUBSCRIPTIONTYPE_FREE, 
   SUBSCRIPTIONTYPE_STARTERCHAT, 
   SUBSCRIPTIONTYPE_STARTERVOICE,
   PREMIUM_SUBSCRIPTION_TYPES
  } from 'constants/PremiumConstant';
import { 
  canStarterVoiceSeeChatFeatures,
  getAddonFeaturesFromToken,
  getSubscriptionFeaturesFromToken,
} from 'utils/jwtUtils';
import { safeGetToken } from 'utils/safeStorage';

// Helper function to get current subscription status
const getCurrentSubscriptionStatus = () => {
  const token = safeGetToken(SEC_AUTH_M_TK);
  const userRole = getUserRole();
  const canStarterVoiceSeeChat = canStarterVoiceSeeChatFeatures(token);
  let hasValidSubscription = false;
  let subscriptionType = SUBSCRIPTIONTYPE_FREE;
  
  if (token && typeof token === 'string') {
    try {
      // Validate JWT token format
      const tokenParts = token.split('.');
      if (tokenParts.length === 3) {
        const payload = tokenParts[1];
        // Decode the base64 payload
        const decodedPayload = atob(payload);
        // Parse only if we have a valid string
        if (decodedPayload && typeof decodedPayload === 'string') {
          const decoded = JSON.parse(decodedPayload);
          if (decoded && typeof decoded === 'object') {
            subscriptionType = decoded.subscription_type || SUBSCRIPTIONTYPE_FREE;
            hasValidSubscription = PREMIUM_SUBSCRIPTION_TYPES.includes(subscriptionType);
          }
        }
      }
    } catch (error) {
      console.warn('Error parsing JWT token in NavigationConfig:', error);
      hasValidSubscription = false;
      subscriptionType = SUBSCRIPTIONTYPE_FREE;
    }
  }
  
  return {
    userRole,
    subscriptionType,
    hasValidSubscription,
    canStarterVoiceSeeChat
  };
};

// ============================================================================
// HELPER FUNCTIONS (SonarQube fix - reduce cognitive complexity)
// ============================================================================

/**
 * Check if user has premium access (including starter subscriptions)
 * @param {boolean} currentHasValidSubscription - Whether user has valid premium subscription
 * @param {string} subscriptionType - Current subscription type
 * @returns {boolean} True if user has premium access
 */
const hasPremiumAccess = (currentHasValidSubscription, subscriptionType) => {
  return currentHasValidSubscription || 
         subscriptionType === SUBSCRIPTIONTYPE_STARTERCHAT || 
         subscriptionType === SUBSCRIPTIONTYPE_STARTERVOICE;
};

/**
 * Check if user role has admin/checker access
 * @param {string} currentUserRole - Current user role
 * @returns {boolean} True if user is Admin or Checker
 */
const isAdminOrChecker = (currentUserRole) => {
  return currentUserRole === 'Admin' || currentUserRole === 'Checker';
};

/**
 * Check if user role has admin/checker/maker access
 * @param {string} currentUserRole - Current user role
 * @returns {boolean} True if user is Admin, Checker, or Maker
 */
const isAdminCheckerOrMaker = (currentUserRole) => {
  return currentUserRole === 'Admin' || 
         currentUserRole === 'Checker' || 
         currentUserRole === 'Maker';
};

/**
 * Check if user can see chat agents
 * @param {string} subscriptionType - Current subscription type
 * @param {boolean} canStarterVoiceSeeChat - Whether Starter_Voice can see chat
 * @param {boolean} currentHasValidSubscription - Whether user has valid premium subscription
 * @returns {boolean} True if user can see chat agents
 */
const canSeeChatAgents = (subscriptionType, canStarterVoiceSeeChat, currentHasValidSubscription) => {
  return subscriptionType === SUBSCRIPTIONTYPE_STARTERCHAT ||
         subscriptionType === SUBSCRIPTIONTYPE_FREE ||
         (subscriptionType === SUBSCRIPTIONTYPE_STARTERVOICE && canStarterVoiceSeeChat) ||
         (currentHasValidSubscription && subscriptionType !== SUBSCRIPTIONTYPE_STARTERVOICE);
};

/**
 * Basic plan: WhatsApp Voice AI Agent add-on unlocks voice agent sidebar
 */
const hasWhatsAppVoiceEntitlement = () => {
  const token = safeGetToken(SEC_AUTH_M_TK);
  const features = [
    ...getAddonFeaturesFromToken(token),
    ...getSubscriptionFeaturesFromToken(token),
  ];
  return features.some((f) => String(f || '').toLowerCase() === 'whatsappvoiceagent');
};

const hasFusionAddon = () => {
  const token = safeGetToken(SEC_AUTH_M_TK);
  return (getAddonFeaturesFromToken(token) || []).some((f) =>
    String(f || '').toLowerCase().includes('fusion')
  );
};

/**
 * Check if user can see voice agents
 * @param {string} subscriptionType - Current subscription type
 * @returns {boolean} True if user can see voice agents
 */
const canSeeVoiceAgents = (subscriptionType) => {
  if (subscriptionType === SUBSCRIPTIONTYPE_STARTERCHAT) {
    return hasWhatsAppVoiceEntitlement();
  }
  return subscriptionType !== SUBSCRIPTIONTYPE_STARTERCHAT;
};

/**
 * Build Analytics menu item conditionally
 * @param {string} currentUserRole - Current user role
 * @param {boolean} currentHasValidSubscription - Whether user has valid premium subscription
 * @param {string} subscriptionType - Current subscription type
 * @returns {Array} Array containing analytics menu item or empty array
 */
const buildAnalyticsMenuItem = (currentUserRole, currentHasValidSubscription, subscriptionType) => {
  if (isAdminOrChecker(currentUserRole) && hasPremiumAccess(currentHasValidSubscription, subscriptionType)) {
    return [
      {
        key: 'dashboards-analytic',
        path: `${APP_PREFIX_PATH}/dashboards/analytic`,
        title: 'sidenav.dashboard.analytic',
        icon: LayoutGrid,
        breadcrumb: true,
        submenu: []
      }
    ];
  }
  return [];
};

/**
 * Build Voice Agents menu item conditionally
 * @param {string} subscriptionType - Current subscription type
 * @returns {Array} Array containing voice agents menu item or empty array
 */
const buildVoiceAgentsMenuItem = (subscriptionType) => {
  if (canSeeVoiceAgents(subscriptionType)) {
    return [{
      key: 'dashboards-bot',
      path: `${APP_PREFIX_PATH}/agent`,
      title: 'sidenav.dashboard.agent',
      icon: AudioLines,
      breadcrumb: true,
      submenu: []
    }];
  }
  return [];
};

/**
 * Build Chat Agents menu item conditionally
 * @param {string} subscriptionType - Current subscription type
 * @param {boolean} canStarterVoiceSeeChat - Whether Starter_Voice can see chat
 * @param {boolean} currentHasValidSubscription - Whether user has valid premium subscription
 * @returns {Array} Array containing chat agents menu item or empty array
 */
const buildChatAgentsMenuItem = (subscriptionType, canStarterVoiceSeeChat, currentHasValidSubscription) => {
  if (canSeeChatAgents(subscriptionType, canStarterVoiceSeeChat, currentHasValidSubscription)) {
    return [{
      key: 'dashboards-chat-agent',
      path: `${APP_PREFIX_PATH}/chat-agent`,
      title: 'Chat Agents',
      icon: Bot,
      breadcrumb: true,
      submenu: []
    }];
  }
  // Standard (Starter_Voice) without any chat add-on: show disabled menu item with reason
  if (subscriptionType === SUBSCRIPTIONTYPE_STARTERVOICE && !canStarterVoiceSeeChat) {
    return [{
      key: 'dashboards-chat-agent',
      path: `${APP_PREFIX_PATH}/chat-agent`,
      title: 'Chat Agents',
      icon: Bot,
      breadcrumb: true,
      submenu: [],
      disabled: true,
      disabledReason: 'Addon not purchased'
    }];
  }
  return [];
};

/**
 * Build Tools menu item conditionally
 * @param {boolean} currentHasValidSubscription - Whether user has valid premium subscription
 * @param {string} subscriptionType - Current subscription type
 * @returns {Array} Array containing tools menu item or empty array
 */
const buildToolsMenuItem = (currentHasValidSubscription, subscriptionType) => {
  if (hasPremiumAccess(currentHasValidSubscription, subscriptionType)) {
    return [{
      key: 'add-tool',
      path: `${APP_PREFIX_PATH}/add-tool`,
      title: 'sidenav.dashboard.addTool',
      icon: Blocks,
      breadcrumb: false,
      isGroupTitle: false,
      submenu: []
    }];
  }
  return [];
};

const buildCustomToolsMenuItem = (currentHasValidSubscription, subscriptionType) => {
  if (hasPremiumAccess(currentHasValidSubscription, subscriptionType)) {
    return [{
      key: 'custom-tools',
      path: `${APP_PREFIX_PATH}/custom-tools`,
      title: 'sidenav.dashboard.customToolBuilder',
      icon: Blocks,
      breadcrumb: false,
      submenu: []
    }];
  }
  return [];
};

/**
 * Build Access Control menu item conditionally
 * @param {string} currentUserRole - Current user role
 * @param {boolean} currentHasValidSubscription - Whether user has valid premium subscription
 * @param {string} subscriptionType - Current subscription type
 * @returns {Array} Array containing access control menu item or empty array
 */
const buildAccessControlMenuItem = (currentUserRole, currentHasValidSubscription, subscriptionType) => {
  if (currentUserRole === 'Admin' && hasPremiumAccess(currentHasValidSubscription, subscriptionType)) {
    return [{
      key: 'dashboards-accesscontrol',
      path: `${APP_PREFIX_PATH}/accesscontrol`,
      title: 'sidenav.dashboard.accesscontrol',
      icon: Fingerprint,
      breadcrumb: true,
      submenu: []
    }];
  }
  return [];
};

/**
 * Build Context Data menu item conditionally
 * @param {boolean} currentHasValidSubscription - Whether user has valid premium subscription
 * @param {string} subscriptionType - Current subscription type
 * @returns {Array} Array containing context data menu item or empty array
 */
const buildContextDataMenuItem = (currentHasValidSubscription, subscriptionType) => {
  if (hasPremiumAccess(currentHasValidSubscription, subscriptionType) || subscriptionType === SUBSCRIPTIONTYPE_FREE) {
    return [{
      key: 'dashboards-data',
      path: `${APP_PREFIX_PATH}/data`,
      title: 'sidenav.dashboard.info.contextData',
      icon: Database,
      breadcrumb: true,
      submenu: []
    }];
  }
  return [];
};

/**
 * Build Document Approval menu item conditionally
 * @param {string} currentUserRole - Current user role
 * @param {boolean} currentHasValidSubscription - Whether user has valid premium subscription
 * @param {string} subscriptionType - Current subscription type
 * @returns {Array} Array containing document approval menu item or empty array
 */
const buildDocumentApprovalMenuItem = (currentUserRole, currentHasValidSubscription, subscriptionType) => {
  if (isAdminCheckerOrMaker(currentUserRole) && hasPremiumAccess(currentHasValidSubscription, subscriptionType)) {
    return [{
      key: 'dashboards-document_approval',
      path: `${APP_PREFIX_PATH}/document_approval`,
      title: 'sidenav.dashboard.document_approval',
      icon: FileCheck2,
      breadcrumb: true,
      submenu: []
    }];
  }
  return [];
};

/**
 * Build Voice Agent Approval menu item conditionally
 * @param {string} currentUserRole - Current user role
 * @param {boolean} currentHasValidSubscription - Whether user has valid premium subscription
 * @param {string} subscriptionType - Current subscription type
 * @returns {Array} Array containing voice agent approval menu item or empty array
 */
const buildVoiceAgentApprovalMenuItem = (currentUserRole, currentHasValidSubscription, subscriptionType) => {
  if (isAdminCheckerOrMaker(currentUserRole)) {
    const canSee = subscriptionType === SUBSCRIPTIONTYPE_STARTERVOICE ||
                   (subscriptionType === SUBSCRIPTIONTYPE_STARTERCHAT && hasWhatsAppVoiceEntitlement()) ||
                   (currentHasValidSubscription && subscriptionType !== SUBSCRIPTIONTYPE_STARTERCHAT);
    if (canSee) {
      return [{
        key: 'dashboards-voice-agent-approval',
        path: `${APP_PREFIX_PATH}/voice-agent-approval`,
        title: 'sidenav.dashboard.agent_approval',
        icon: MicVocal,
        breadcrumb: true,
        submenu: []
      }];
    }
  }
  return [];
};

/**
 * Build Chat Agent Approval menu item conditionally
 * @param {string} currentUserRole - Current user role
 * @param {string} subscriptionType - Current subscription type
 * @param {boolean} canStarterVoiceSeeChat - Whether Starter_Voice can see chat
 * @param {boolean} currentHasValidSubscription - Whether user has valid premium subscription
 * @returns {Array} Array containing chat agent approval menu item or empty array
 */
const buildChatAgentApprovalMenuItem = (currentUserRole, subscriptionType, canStarterVoiceSeeChat, currentHasValidSubscription) => {
  if (isAdminCheckerOrMaker(currentUserRole)) {
    const canSee = subscriptionType === SUBSCRIPTIONTYPE_STARTERCHAT ||
                   (subscriptionType === SUBSCRIPTIONTYPE_STARTERVOICE && canStarterVoiceSeeChat) ||
                   (currentHasValidSubscription && subscriptionType !== SUBSCRIPTIONTYPE_STARTERVOICE);
    if (canSee) {
      return [{
        key: 'dashboards-chat-agent-approval',
        path: `${APP_PREFIX_PATH}/chat-agent-approval`,
        title: 'Chat Agent Approval',
        icon: MessageSquareDiff,
        breadcrumb: true,
        submenu: []
      }];
    }
  }
  return [];
};

/**
 * Build Tool Approval menu item conditionally
 * @param {string} currentUserRole - Current user role
 * @param {boolean} currentHasValidSubscription - Whether user has valid premium subscription
 * @param {string} subscriptionType - Current subscription type
 * @returns {Array} Array containing tool approval menu item or empty array
 */
const buildToolApprovalMenuItem = (currentUserRole, currentHasValidSubscription, subscriptionType) => {
  if (isAdminCheckerOrMaker(currentUserRole) && hasPremiumAccess(currentHasValidSubscription, subscriptionType)) {
    return [{
      key: 'dashboards-tool-approval',
      path: `${APP_PREFIX_PATH}/tool-approval`,
      title: 'Tool Approval',
      icon: Wrench,
      breadcrumb: true,
      submenu: []
    }];
  }
  return [];
};

/**
 * Build Audit Logs menu item conditionally
 * @param {string} currentUserRole - Current user role
 * @param {boolean} currentHasValidSubscription - Whether user has valid premium subscription
 * @param {string} subscriptionType - Current subscription type
 * @returns {Array} Array containing audit logs menu item or empty array
 */
const buildAuditLogsMenuItem = (currentUserRole, currentHasValidSubscription, subscriptionType) => {
  if (hasPremiumAccess(currentHasValidSubscription, subscriptionType)) {
    return [{
      key: 'dashboards-audit-logs',
      path: `${APP_PREFIX_PATH}/audit-logs`,
      title: 'Audit Logs',
      icon: History,
      breadcrumb: true,
      submenu: []
    }];
  }
  return [];
};

/**
 * Build Workspace submenu
 * @param {string} currentUserRole - Current user role
 * @param {boolean} currentHasValidSubscription - Whether user has valid premium subscription
 * @param {string} subscriptionType - Current subscription type
 * @returns {Array} Workspace submenu items
 */
const buildWorkspaceSubmenu = (currentUserRole, currentHasValidSubscription, subscriptionType) => {
  return [
    {
      key: 'app-home',
      path: `${APP_PREFIX_PATH}/home`,
      title: 'sidenav.dashboard.home',
      icon: Home,
      breadcrumb: true,
      submenu: []
    },
    {
      key: 'dashboards-default',
      path: `${APP_PREFIX_PATH}/dashboards/default`,
      title: 'sidenav.dashboard.default',
      icon: LayoutDashboard,
      breadcrumb: true,
      submenu: []
    },
    {
      key: 'dashboards-search',
      path: `${APP_PREFIX_PATH}/search`,
      title: 'sidenav.dashboard.search',
      icon: FlaskConical,
      breadcrumb: true,
      submenu: []
    },
    ...buildAnalyticsMenuItem(currentUserRole, currentHasValidSubscription, subscriptionType)
  ];
};

/**
 * Build AI & Automation submenu
 * @param {string} subscriptionType - Current subscription type
 * @param {boolean} canStarterVoiceSeeChat - Whether Starter_Voice can see chat
 * @param {boolean} currentHasValidSubscription - Whether user has valid premium subscription
 * @returns {Array} AI & Automation submenu items
 */
const buildAIAutomationSubmenu = (subscriptionType, canStarterVoiceSeeChat, currentHasValidSubscription) => {
  return [
    {
      key: 'dashboards-knowledgeBase',
      path: `${APP_PREFIX_PATH}/kb`,
      title: 'sidenav.dashboard.knowledgeBase',
      icon: BrainCircuit,
      breadcrumb: true,
      submenu: []
    },
    ...buildVoiceAgentsMenuItem(subscriptionType),
    ...buildChatAgentsMenuItem(subscriptionType, canStarterVoiceSeeChat, currentHasValidSubscription),
    ...buildToolsMenuItem(currentHasValidSubscription, subscriptionType),
    ...buildCustomToolsMenuItem(currentHasValidSubscription, subscriptionType),
    ...(hasPremiumAccess(currentHasValidSubscription, subscriptionType) ? [{
      key: 'vlm-dev-portal',
      path: `${APP_PREFIX_PATH}/vlm-dev-portal/home`,
      title: 'VLM Dev Portal',
      icon: Blocks,
      breadcrumb: true,
      submenu: []
    }] : []),
    ...(hasFusionAddon() ? [{
      key: 'fusion-plus',
      path: `${APP_PREFIX_PATH}/fusion-plus`,
      title: 'Fusion+',
      icon: Sparkles,
      breadcrumb: false,
      submenu: []
    }] : []),
  ];
};

/**
 * Build Collaboration submenu
 * @param {string} currentUserRole - Current user role
 * @param {boolean} currentHasValidSubscription - Whether user has valid premium subscription
 * @param {string} subscriptionType - Current subscription type
 * @param {boolean} canStarterVoiceSeeChat - Whether Starter_Voice can see chat
 * @returns {Array} Collaboration submenu items
 */
const buildCollaborationSubmenu = (currentUserRole, currentHasValidSubscription, subscriptionType, canStarterVoiceSeeChat) => {
  return [
    ...buildAccessControlMenuItem(currentUserRole, currentHasValidSubscription, subscriptionType),
    ...buildContextDataMenuItem(currentHasValidSubscription, subscriptionType),
    ...buildDocumentApprovalMenuItem(currentUserRole, currentHasValidSubscription, subscriptionType),
    ...buildVoiceAgentApprovalMenuItem(currentUserRole, currentHasValidSubscription, subscriptionType),
    ...buildChatAgentApprovalMenuItem(currentUserRole, subscriptionType, canStarterVoiceSeeChat, currentHasValidSubscription),
    ...buildToolApprovalMenuItem(currentUserRole, currentHasValidSubscription, subscriptionType),
    ...buildAuditLogsMenuItem(currentUserRole, currentHasValidSubscription, subscriptionType)
  ];
};

/**
 * Build Account & Settings submenu
 * @param {boolean} currentHasValidSubscription - Whether user has valid premium subscription
 * @param {string} subscriptionType - Current subscription type
 * @returns {Array} Account & Settings submenu items
 */
const buildAccountSettingsSubmenu = (currentHasValidSubscription, subscriptionType) => {
  const usageAnalyticsItem = hasPremiumAccess(currentHasValidSubscription, subscriptionType) ? [{
    key: 'payment-dashboard',
    path: `${APP_PREFIX_PATH}/payment/dashboard`,
    title: 'Usage Analytics',
    icon: Activity,
    breadcrumb: true,
    submenu: [],
    meta: { roles: ['Admin'] }
  }] : [];

  const billingHistoryItem = subscriptionType !== SUBSCRIPTIONTYPE_FREE ? [{
    key: 'payment-history',
    path: `${APP_PREFIX_PATH}/payment/history`,
    title: 'Billing & History',
    icon: History,
    breadcrumb: true,
    submenu: [],
    meta: { roles: ['Admin'] }
  }] : [];

  return [
    ...usageAnalyticsItem,
    ...billingHistoryItem,
    {
      key: 'payment-pricing',
      path: `${APP_PREFIX_PATH}/pages/pricing`,
      title: 'Upgrade Plans',
      icon: Crown,
      breadcrumb: true,
      submenu: [],
      meta: { roles: ['Admin'] }
    }
  ];
};

// Note: Navigation config is now generated dynamically in getNavigationConfig function

const getNavigationConfig = (userRoleParam, hasValidSubscriptionParam) => {
  // Get current subscription status dynamically
  const {
    userRole,
    subscriptionType,
    hasValidSubscription,
    canStarterVoiceSeeChat
  } = getCurrentSubscriptionStatus();
  
  // Use parameters if provided, otherwise use current values
  const currentUserRole = userRoleParam || userRole;
  const currentHasValidSubscription = hasValidSubscriptionParam !== undefined ? hasValidSubscriptionParam : hasValidSubscription;
  
  // Debug logging for permission issues
  console.log('NavigationConfig Debug:', {
    subscriptionType,
    hasValidSubscription,
    currentHasValidSubscription,
    currentUserRole,
    canStarterVoiceSeeChat
  });

  const collaborationSubmenu = buildCollaborationSubmenu(
    currentUserRole,
    currentHasValidSubscription,
    subscriptionType,
    canStarterVoiceSeeChat
  );
  
  // Build navigation config dynamically based on current state
  const navigationConfig = [
    {
      key: 'workspace',
      path: `${APP_PREFIX_PATH}/workspace`,
      title: 'Workspace',
      icon: LayoutDashboard,
      breadcrumb: false,
      isGroupTitle: true,
      submenu: buildWorkspaceSubmenu(currentUserRole, currentHasValidSubscription, subscriptionType)
    },
    {
      key: 'ai-automation',
      path: '',
      title: 'AI & Automation',
      icon: BrainCircuit,
      breadcrumb: false,
      isGroupTitle: true,
      submenu: buildAIAutomationSubmenu(subscriptionType, canStarterVoiceSeeChat, currentHasValidSubscription)
    },
    ...(collaborationSubmenu.length > 0 ? [{
      key: 'collaboration',
      path: '',
      title: 'Collaboration',
      icon: ShieldCheck,
      breadcrumb: false,
      isGroupTitle: true,
      submenu: collaborationSubmenu
    }] : []),
    ...(currentUserRole === 'Admin' ? [{
      key: 'account-settings',
      path: '',
      title: 'Account & Settings',
      icon: Activity,
      breadcrumb: true,
      isGroupTitle: true,
      submenu: buildAccountSettingsSubmenu(currentHasValidSubscription, subscriptionType)
    }] : []),
    {
      key: 'help-support',
      path: '',
      title: 'Help & Support',
      icon: HelpCircle,
      breadcrumb: false,
      isGroupTitle: true,
      submenu: [
        {
          key: 'dashboards-help',
          path: `${APP_PREFIX_PATH}/help`,
          title: 'sidenav.dashboard.info.helps',
          icon: HelpCircle,
          breadcrumb: true,
          submenu: []
        },
        {
          key: 'dashboards-about',
          path: `${APP_PREFIX_PATH}/about`,
          title: 'sidenav.dashboard.info.about',
          icon: Info,
          breadcrumb: true,
          submenu: []
        },
        {
          key: 'dashboards-contacts',
          path: `${APP_PREFIX_PATH}/contact`,
          title: 'sidenav.dashboard.info.contacts',
          icon: Mail,
          breadcrumb: true,
          submenu: []
        }
      ]
    }
  ];
  
  return navigationConfig;
};

export default getNavigationConfig;